import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldContent,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
} from "@beweegbaas/ui";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@beweegbaas/ui";
import { KLASSEN_OVERVIEW_QUERY_KEY } from "#/modules/school.klassen/query.klassen-overview";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { SubmitButton } from "#/components/submit-button";
import { useAppForm } from "#/modules/util.forms/form";
import {
  getKlasModalData,
  klasFormSchema,
  submitUpdateKlas,
} from "#/modules/school.klassen/classes.functions";
import { getLeerjaarTranslation } from "#/modules/util.i18n/enum-mapping";
import { m } from "#/paraglide/messages";
import { Leerjaar } from "#/schema.nswag";
import { fieldErrorText } from "#/components/form-errors";
import { z } from "zod/v4";

type KlasFormValues = z.input<typeof klasFormSchema>;
const LEERJAAR_OPTIONS = Object.values(Leerjaar).filter((l) => l !== Leerjaar.None);

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/classes/$klasId/modal",
)({
  loader: ({ params }) => getKlasModalData({ data: { klasId: params.klasId } }),
  component: KlasEditModal,
  errorComponent: KlasModalError,
});

function KlasEditModal() {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { locale, schoolyear, school } = Route.useParams();
  const { klas } = Route.useLoaderData();
  const submitUpdateKlasFn = useServerFn(submitUpdateKlas);

  // Close by navigating up to the classes list (two segments up: `$klasId/modal` -> `classes`).
  const onClose = () =>
    navigate({
      to: "/$locale/{-$schoolyear}/$school/classes",
      params: { locale, schoolyear, school },
      search: (prev) => prev,
      replace: true,
    });

  const defaultValues: KlasFormValues = {
    klasId: klas?.id ?? "",
    name: klas?.name ?? "",
    leerjaren: klas?.leerjaren ?? [],
  };

  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: klasFormSchema },
    onSubmit: async ({ value }) => {
      const result = await submitUpdateKlasFn({
        data: {
          klasId: value.klasId,
          name: value.name,
          leerjaren: value.leerjaren,
        },
      });

      if (result && ("formError" in result || "fieldErrors" in result)) {
        form.setErrorMap({
          onSubmit: { form: result.formError, fields: result.fieldErrors ?? {} },
        });
        return;
      }

      toast.success(m["klassen.edit_confirm.success_toast"]());
      // The overview list stays mounted behind this modal and reads via TanStack Query —
      // invalidate its keys so it refetches; router.invalidate() refreshes the loader snapshot.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [{ school, schoolyear }, KLASSEN_OVERVIEW_QUERY_KEY],
        }),
        router.invalidate(),
      ]);
      void onClose();
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) void onClose();
      }}
    >
      <DialogContent className="md:max-w-lg!" showCloseButton>
        <DialogHeader>
          <DialogTitle>{m["klassen.bewerk"]()}</DialogTitle>
        </DialogHeader>
        <form.AppForm>
          <form.FormRoot className="flex flex-col gap-4">
            <FieldGroup className="gap-4">
              <form.Field
                name="name"
                children={(field) => {
                  const isInvalid = !field.state.meta.isValid && field.state.meta.isTouched;
                  return (
                    <Field orientation="responsive" data-invalid={isInvalid}>
                      <FieldContent className="min-w-40 flex-none shrink-0 grow-0!">
                        <FieldLabel htmlFor={field.name}>{m["common.fields.name"]()}</FieldLabel>
                      </FieldContent>
                      <div className="flex max-w-sm shrink-0 flex-col gap-2">
                        <Input
                          id={field.name}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          autoComplete="off"
                        />
                        {isInvalid && (
                          <FieldError>{fieldErrorText(field.state.meta.errors)}</FieldError>
                        )}
                      </div>
                    </Field>
                  );
                }}
              />
              <form.Field
                name="leerjaren"
                children={(field) => (
                  <Field orientation="responsive">
                    <FieldContent className="min-w-40 flex-none shrink-0 grow-0!">
                      <FieldLabel>Leerjaren</FieldLabel>
                    </FieldContent>
                    <div className="grid grid-cols-2 gap-3">
                      {LEERJAAR_OPTIONS.map((leerjaar) => (
                        <label key={leerjaar} className="flex cursor-pointer items-center gap-2">
                          <Checkbox
                            checked={field.state.value.includes(leerjaar)}
                            onCheckedChange={(checked) => {
                              const next = checked
                                ? [...field.state.value, leerjaar]
                                : field.state.value.filter((l) => l !== leerjaar);
                              field.handleChange(next);
                            }}
                          />
                          <span className="text-sm">{getLeerjaarTranslation(leerjaar)}</span>
                        </label>
                      ))}
                      {!field.state.meta.isValid && (
                        <FieldError className="col-span-2">
                          {fieldErrorText(field.state.meta.errors)}
                        </FieldError>
                      )}
                    </div>
                  </Field>
                )}
              />
            </FieldGroup>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={onClose}>
                Annuleren
              </Button>
              <form.Subscribe
                selector={(state) => [state.canSubmit, state.isSubmitting]}
                children={([canSubmit]) => (
                  <SubmitButton type="submit" disabled={!canSubmit}>
                    {m["common.actions.save"]()}
                  </SubmitButton>
                )}
              />
            </DialogFooter>
          </form.FormRoot>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}

function KlasModalError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
